import React from 'react';
import { useToggleObject } from '../hooks';
import { useCreateUserConversationMutation, useUserConversationsQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { useFeatureFlag } from '@uplift/hooks';


export type MessagingContextProps = {
  modalOpened: boolean;
  openModal: (conversationId?: string, messageId?: string) => void;
  closeModal: () => void;
  unreadMessagesCount: number;
  selectedConversationId?: string;
  setSelectedConversationId: (id?: string) => void;
  focusedMessageId?: string;
  setFocusedMessageId: (messageId?: string) => void;
  createChat: (participantId: string) => Promise<string>;
};

const defaultValue = {
  modalOpened: false,
  openModal: () => undefined,
  closeModal: () => undefined,
  unreadMessagesCount: 0,
  focusedMessageId: undefined,
  setFocusedMessageId: () => undefined,
  selectedConversationId: undefined,
  setSelectedConversationId: () => undefined,
  createChat: async () => '',
};
const MessagingContext = React.createContext<MessagingContextProps>(defaultValue);


export const useMessagingContext = () => {
  const context = React.useContext(MessagingContext);

  if (context === defaultValue) {
    console.warn('useMessagingContext must be used within a MessagingProvider');
  }

  return context;
};

export const useSelectedConversation = () => {
  const messaging = useMessagingContext();
  const { data: conversations = [] } = useUserConversationsQuery({ enabled: messaging.modalOpened });
  const selectedConversation = React.useMemo(() => conversations.find(c => c.id === messaging.selectedConversationId), [ conversations, messaging.selectedConversationId ]);

  return selectedConversation;
};


export const MessagingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const me = useCurrentuser();
  const toggle = useToggleObject(false);
  const IN_APP_MESSAGING = useFeatureFlag('IN_APP_MESSAGING');
  const createConversationMutation = useCreateUserConversationMutation();
  const [ selectedConversationId, setSelectedConversationId ] = React.useState<string | undefined>(undefined);
  const [ focusedMessageId, setFocusedMessageId ] = React.useState<string | undefined>(undefined);
  const { data: conversations = [] } = useUserConversationsQuery({ enabled: !!me.id && !!IN_APP_MESSAGING });
  const unreadMessagesCount = React.useMemo(() => {
    return conversations.reduce((count, conversation) => {
      return count + (conversation.unreadMessagesCount || 0);
    }, 0);
  }, [ conversations ]);

  return (
    <MessagingContext.Provider value={{
      selectedConversationId,
      setSelectedConversationId,
      focusedMessageId,
      setFocusedMessageId,
      modalOpened: toggle.value,
      openModal: (conversationId?: string, messageId?: string) => {
        if (conversationId) {
          setSelectedConversationId(conversationId);
        }
        if (messageId) {
          setFocusedMessageId(messageId);
        }
        toggle.open();
      },
      closeModal: toggle.close,
      unreadMessagesCount: unreadMessagesCount,
      createChat: async (participantId) => {
        const response = await createConversationMutation.mutateAsync({ participantId });

        return response.data.conversationId;
      }
    }}
    >
      {children}
    </MessagingContext.Provider>
  );
};
